import { resolveFieldState } from '@main/complykit/components/field/field-state';
import { memo, ReactNode, useId, useMemo } from 'react';
import { FieldError, get, useFormState } from 'react-hook-form';

import {
  AnyDynamicFormComponentRegistry,
  DynamicFormComponentProps,
  DynamicFormFieldConfig,
} from './field-registry';
import { DynamicFieldState, DynamicFieldStateInput } from './types';

/** Turns a failed rule into the message the field shows; the message restates the rule. */
export type DynamicFormErrorMessage<TRegistry extends AnyDynamicFormComponentRegistry> = (
  error: FieldError,
  config: DynamicFormFieldConfig<TRegistry>,
) => string | undefined;

export interface DynamicFieldProps<
  TRegistry extends AnyDynamicFormComponentRegistry,
  TConfig extends DynamicFormFieldConfig<TRegistry>,
  TProps extends object = object,
> extends Omit<DynamicFormComponentProps<TConfig>, 'state' | 'labelId'> {
  componentRegistry: TRegistry;
  /** What the whole form is: every field starts from it. */
  fieldState?: DynamicFieldStateInput;
  errorMessage?: DynamicFormErrorMessage<TRegistry>;
  wrapper?: DynamicFieldWrapper<TRegistry, TProps>;
  wrapperProps?: TProps;
}

export interface DynamicFieldWrapper<
  TRegistry extends AnyDynamicFormComponentRegistry,
  TProps extends object = object,
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
> extends Function {
  (props: DynamicFieldWrapperProps<TRegistry> & TProps): ReactNode;
  /** What this field is within the form: a completed answer is read-only. */
  resolveState?(
    config: DynamicFormFieldConfig<TRegistry>,
    formState: DynamicFieldStateInput,
    props?: TProps,
  ): DynamicFieldStateInput;
}

export interface DynamicFieldWrapperProps<TRegistry extends AnyDynamicFormComponentRegistry> {
  config: DynamicFormFieldConfig<TRegistry>;
  state: DynamicFieldState;
  /** The id the wrapper gives the element that names the field; the field's control points at it. */
  labelId: string;
  children: ReactNode;
}

const defaultErrorMessage = (error: FieldError) => error.message;

export function DynamicField_<
  TRegistry extends AnyDynamicFormComponentRegistry,
  TConfig extends DynamicFormFieldConfig<TRegistry>,
  TProps extends object = object,
>(props: DynamicFieldProps<TRegistry, TConfig, TProps>) {
  const { errors } = useFormState({ name: props.config.name });
  const labelId = useId();
  const fieldLabelId = props.wrapper ? labelId : undefined;
  const FieldComponent = props.componentRegistry.get(props.config.kind);

  const Wrapper: DynamicFieldWrapper<TRegistry, TProps> = useMemo(
    () => props.wrapper ?? (({ children }) => children),
    [props.wrapper],
  );

  const formState = props.fieldState ?? {};
  const adjusted = Wrapper.resolveState?.(props.config, formState, props.wrapperProps) ?? formState;
  const error = get(errors, props.config.name) as FieldError | undefined;
  // A rule that fails without a message still marks the field invalid.
  const message = error
    ? (props.errorMessage ?? defaultErrorMessage)(error, props.config) || error.type
    : undefined;
  const {
    isDisabled,
    isReadOnly,
    isRequired,
    error: shown,
  } = resolveFieldState({
    isDisabled: adjusted.isDisabled,
    isReadOnly: adjusted.isReadOnly,
    isRequired: !!props.config.validation?.required,
    error: message,
  });
  const state = useMemo<DynamicFieldState>(
    () => ({ isDisabled, isReadOnly, isRequired, error: shown }),
    [isDisabled, isReadOnly, isRequired, shown],
  );

  const field = useMemo(
    () => <FieldComponent config={props.config} state={state} labelId={fieldLabelId} />,
    [FieldComponent, props.config, state, fieldLabelId],
  );

  return (
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    <Wrapper {...props.wrapperProps!} config={props.config} state={state} labelId={labelId}>
      {field}
    </Wrapper>
  );
}

export const DynamicField = memo(DynamicField_) as typeof DynamicField_;
